<template lang="pug">
el-container(id="UserEdit-container")
  el-dialog(id="UserEdit-dialog-wrapper", :visible.sync="mutableEditDialogVisible", fullscreen=false, :title="$t(userData.id ? 'Edit user' : 'Add user')", :before-close="beforeCloseHandler", :close-on-click-modal="false", append-to-body)
    el-form(
      id="UserEdit-form"
      :model="userData",
      :rules="rules",
      ref="userForm",
      label-width="220px",
      status-icon=true,
      :size="uxParam('formSize')",
      v-loading="loading",
    )
      el-form-item(id="UserEdit-form-nick" :label="$t('Nick')", prop="nick")
        el-input(id="UserEdit-form-nickInput" v-model="userData.nick")
      el-form-item(id="UserEdit-form-email" :label="$t('Email')", prop="email")
        el-input(id="UserEdit-form-emailInput" v-model="userData.email", type="email")
      el-form-item(id="UserEdit-form-skin" :label="$t('Skin')", prop="skin")
        el-select.el-select-width-100(id="UserEdit-form-skinSelect" v-model='userData.skin')
          el-option(
            v-for="item of skinList",
            :key="item",
            :value="item",
            v-bind:id="'skinOption' + item"
          )
      el-tooltip(
        id="UserEdit-form-tooltip"
        effect="light",
        :content="$t('Change client will clean actual networks')",
        placement="top",
        :transition='uxParam("tooltipTransition")',
        :open-delay='uxParam("tooltipActivationDelay")'
      )
        el-form-item(id="UserEdit-form-client" :label="$t('Client')", prop="ClientId")
          el-select.el-select-width-100(id="UserEdit-form-clientSelect" v-model='userData.ClientId', @change="clientChangeHandler")
            el-option(
              v-for="item in clientList",
              :key="item.id",
              :label="item.name",
              :value="item.id",
              v-bind:id="'clientOption' + item.id"
            )
      el-form-item(id="UserEdit-form-networks" :label="$t('Client Networks')", prop="NetworkIds")
        el-select.el-select-width-100(id="UserEdit-form-networksSelect" v-model='userData.NetworkIds', multiple)
          el-option(
            v-for="item in networkList",
            :key="item.id",
            :label="item.name",
            :value="item.id",
            v-bind:id="'networkOption' + item.id"
          )
      el-form-item(id="UserEdit-form-roles" :label="$t('Roles')", prop="RoleIds")
        el-select.el-select-width-100(id="UserEdit-form-rolesSelect" v-model='userData.RoleIds', multiple)
          el-option(
            v-for="item in roleList",
            :key="item.id",
            :label="item.name",
            :value="item.id",
            v-bind:id="'rolesOption' + item.id"
          )
      el-form-item(id="UserEdit-providers" :label="$t('Security provided by')", prop="Providers")
        el-select.el-select-width-100(id="UserEdit-form-providersSelect" v-model='userData.provider')
          el-option(
              v-for="item in providers",
              :key="item.id",
              :label="item.name",
              :value="item.id",
              v-bind:id="'providerOption' + item.name"
            )
      hr.header
      section(v-if='!userId && userData.provider === "local"')
        el-form-item(id="UserEdit-form-password" :label="$t('Password')", prop="password")
          el-input(id="UserEdit-form-passwordInput" v-model='userData.password', type="password", autocomplete="off", @input='passwordErrorMessage = null')
        el-form-item(id="UserEdit-form-repeatPassword" :label="$t('Retype password')", prop="password2", autocomplete="off")
          el-input(id="UserEdit-form-repeatPasswordInput" v-model='userData.password2', type="password")
        el-form-item(id="UserEdit-form-suggestPassword")
          el-button(type="success", size="mini", @click="suggestPassword()")
            span {{ $t('Suggest new password') }}
          span.warning.selectable &nbsp; {{ suggestedPassword }} &nbsp;
          el-button(v-if='suggestedPassword', type="text", @click="copySuggestedPassword()", :title="$t('Copy password to clipboard')")
            font-awesome-icon(icon="copy")
      section(v-if='userId && userData.provider === "local"')
        el-form-item(id="UserEdit-form-passwordReset" :label="$t('Send reset password link')")
          el-button(type="success", size="mini", @click="sendPasswordRecoveryLink()")
            span {{ $t('Send link') }}
          .warning(v-if='passwordResetResponseStatus === 501') {{ $t('Password recovery is disabled on this instance.') }}
          .danger(v-if='passwordResetResponseStatus && passwordResetResponseStatus !== 200 && passwordResetResponseStatus !== 501') {{ $t('Password link has not been sent due to email error. You may copy link and sent to user by yourself.') }}
          .password-link.selectable(v-if='passwordResetLink') {{ passwordResetLink }}
          el-button(v-if='passwordResetLink', type="text", @click="copyGeneratedPasswordLink()", :title="$t('Copy password reset link to clipboard')")
            font-awesome-icon(icon="copy")
          .warning(v-if='passwordResetResponseStatus === 200') {{ $t('Password reset link has been sent') }}
        el-form-item(id="UserEdit-form-passwordGenerate" :label="$t('Generate new password')")
          el-button(type="success", size="mini", @click="generateAndUpdatePassword()")
            span {{ $t('Generate new password') }}
          span.warning.selectable &nbsp; {{ generatedPassword }} &nbsp;
          el-button(v-if='generatedPassword', type="text", @click="copyGeneratedPassword()", :title="$t('Copy password to clipboard')")
            font-awesome-icon(icon="copy")
      hr.header
      el-form-item(id="UserEdit-form-passwordError" v-if='passwordErrorMessage')
        span.danger(id="UserEdit-form-passwordErrorMessage") {{ $t(passwordErrorMessage) }}
        br
        span.warning(id="UserEdit-form-passwordRequirementsWarning") {{ $t('Password has to meet following requirements:') }}
        .warning-password
            p {{ $t('at least 8 characters in length,') }}
            p {{ $t('contain at least one upper-case character,') }}
            p {{ $t('contain at least one lower-case character,') }}
            p {{ $t('contain at least one digit,') }}
            p {{ $t('contain at least one special character.') }}
      el-form-item(id="UserEdit-form-accountActive" :label="$t('Account active')", prop="deleted")
        el-switch(id="UserEdit-form-accountActiveSwitch" v-model="userData.deleted", inactive-color="#13ce66", active-color="#ff4949")
      el-form-item(id="UserEdit-form-testUser" :label="$t('Test user:')", prop="test")
        el-checkbox(id="UserEdit-form-testUserCheckbox" v-model="userData.test", :disabled='userData.test') {{ $t('Enabled') }}
    .dialog-footer(id="UserEdit-dialogFooter" slot="footer")
      el-button(
        id="UserEdit-form-submitButton",
        type="success",
        @click="submitForm('userForm')",
        size='mini',
        :disabled="loading"
      ) {{$t('Save')}}
</template>

<script>
import Vue from 'vue'
import generator from 'generate-password'
export default {
  props: {
    'userId': {
      required: true
    },
    'editDialogVisible': {
      required: true
    }
  },
  async created () {
    this.providers = (await this.$store.dispatch('User/getAuthProviders'))
  },
  data () {
    let validatePass = (rule, value, callback) => {
      if (value === '' && this.userId === 0) {
        callback(new Error(this.$t('Please input the password')))
      } else {
        if (this.userData.password2 !== '') {
          this.$refs.userForm.validateField('password2')
        }
        callback()
      }
    }
    let validatePass2 = (rule, value, callback) => {
      if (value === '' && (this.userData.password !== '' || this.userId === 0)) {
        callback(new Error(this.$t('Please input the password again')))
      } else if (value !== this.userData.password) {
        callback(new Error(this.$t('Two inputs don\'t match!')))
      } else {
        callback()
      }
    }

    return {
      loading: true,
      providers: [],
      userData: {
        id: 0,
        nick: '',
        email: '',
        password: '',
        password2: '',
        provider: 'local',
        Roles: [],
        RoleIds: [],
        Client: null,
        ClientId: null,
        Networks: [],
        NetworkIds: []
      },
      generatedPassword: null,
      suggestedPassword: null,
      roleList: {},
      clientList: {},
      networkList: {},
      skinList: [
        'full',
        // 'onlyDevices',
        // 'onlyMedia',
        'onlyOwnMedia'
      ],
      passwordErrorMessage: null,
      passwordResetResponseStatus: undefined,
      passwordResetLink: undefined,
      rules: {
        nick: [
          { required: true, message: this.$t('Please enter nick name'), trigger: 'blur' },
          { min: 3, max: 255, message: this.$t('Length should be 3 to 255'), trigger: 'blur' }
        ],
        email: [
          { required: true, message: this.$t('Please enter email'), trigger: 'blur' },
          { min: 3, max: 255, message: this.$t('Length should be 3 to 255'), trigger: 'blur' },
          { required: true, type: 'email', message: this.$t('Email should be valid address'), trigger: 'blur' }
        ],
        ClientId: [
          { required: true, message: this.$t('Please select a client'), trigger: 'change' }
        ],
        RoleIds: [
          { required: true, message: this.$t('Please select assigned roles'), trigger: 'change' },
          { required: true, type: 'array', message: this.$t('Please select assigned roles'), trigger: 'change' }
        ],
        NetworkIds: [
          { required: true, message: this.$t('Please select assigned network'), trigger: 'change' },
          { required: true, type: 'array', message: this.$t('Please select assigned network'), trigger: 'change' }
        ],
        password: [
          { validator: validatePass, trigger: 'blur' }
        ],
        password2: [
          { validator: validatePass2, trigger: 'blur' }
        ]
      }
    }
  },
  watch: {
    userId: async function (newVal, oldVal) {
      this.generatedPassword = null
      this.loading = true
      this.clientList = {}
      this.roleList = {}
      this.networkList = {}
      this.passwordErrorMessage = null
      this.passwordResetResponseStatus = undefined
      this.passwordResetLink = undefined
      this.userData = {
        id: 0,
        nick: '',
        email: '',
        password: '',
        password2: '',
        provider: 'local',
        Roles: [],
        RoleIds: [],
        Client: null,
        ClientId: null,
        Networks: [],
        NetworkIds: []
      }

      this.clientList = await this.$store.dispatch('Client/getClients')
      this.roleList = await this.$store.dispatch('Role/getRoles')
      // ignore superadministrator as option if logged-in user is not a superadministrator
      if (!this.$store.state.User.user.Roles.find(r => r.name === 'superadministrator')) {
        this.roleList.splice(this.roleList.findIndex(role => role.name === 'superadministrator'), 1)
      }
      this.networkList = []

      if (this.userId !== null && this.userId !== 0) {
        let userData = await this.$store.dispatch('User/getUserData', {
          data: {
            userId: this.userId
          }
        })
        this.userData = JSON.parse(JSON.stringify(userData))
        this.clientChangeHandler()
      }

      // this.$refs['userForm'].resetFields()
      this.loading = false
    }
  },
  methods: {
    async sendPasswordRecoveryLink () {
      try {
        const response = await Vue.axios.post('user/sendResetPasswordLinkAdmin', {
          email: this.userData.email
        }, { disableResponseInterceptors: true })
        this.passwordResetResponseStatus = response.status
        this.passwordResetLink = response.data
      } catch (error) {
        console.log(error.response.data.message)
        this.passwordResetResponseStatus = error.response.status
        this.passwordResetLink = error.response.data ? error.response.data.message : ''
      }
    },
    suggestPassword () {
      this.suggestedPassword = generator.generate({
        length: 10,
        numbers: true,
        symbols: true,
        strict: true
      })
    },
    generateAndUpdatePassword () {
      this.generatedPassword = generator.generate({
        length: 10,
        numbers: true,
        symbols: true,
        strict: true
      })
      this.loading = true
      this.$store.dispatch('User/updateUserData', {
        data: { id: this.userId, password: this.generatedPassword, provider: 'local' }
      })
        .catch(error => {
          if (error.response.data.error && error.response.data.error.startsWith('Password')) {
            this.passwordErrorMessage = error.response.data.message
          } else {
            this.$store.dispatch('Error/setAxiosError', error.response)
          }
        })
      this.loading = false
    },
    copySuggestedPassword () {
      this.userData.password = this.suggestedPassword
      this.userData.password2 = this.suggestedPassword
      navigator.clipboard.writeText(this.suggestedPassword)
    },
    copyGeneratedPassword () {
      navigator.clipboard.writeText(this.generatedPassword)
    },
    copyGeneratedPasswordLink () {
      navigator.clipboard.writeText(this.passwordResetLink)
    },
    async submitForm (formName) {
      this.$refs[formName].validate((valid) => {
        if (valid) {
          this.loading = true
          this.$store.dispatch(this.userId === 0 ? 'User/createUserData' : 'User/updateUserData', {
            data: JSON.parse(JSON.stringify(this.userData))
          })
            .then(response => {
              this.mutableEditDialogVisible = false
              this.loading = false
            })
            .catch(error => {
              if (error.response.data.error && error.response.data.error.startsWith('Password')) {
                this.passwordErrorMessage = error.response.data.message
              } else {
                this.$store.dispatch('Error/setAxiosError', error.response)
              }
              this.loading = false
            })
        } else {
          return false
        }
      })
    },

    clientChangeHandler: function (event) {
      this.networkList = this.clientList.find(client => client.id === this.userData.ClientId).Networks
      if (!this.userData.NetworkIds.every(item => this.networkList.find(network => network.id === item) !== undefined)) {
        this.userData.NetworkIds = []
      }
    },

    beforeCloseHandler: function (done) {
      done()
    }
  },
  computed: {
    mutableEditDialogVisible: {
      get: function () {
        return this.editDialogVisible
      },
      set: function (value) {
        this.$emit('closeEditDialog', value)
      }
    }
  }
}
</script>
<style lang="scss" scoped>
@import "../../../theme-m4b/src/common/var.scss";
.warning-password {
  line-height: 3px;
}
.password-link {
  width: 100%;
  font-size: $--font-size-small;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

</style>



// WEBPACK FOOTER //
// src/components/User/UserEdit.vue